import * as THREE from 'three';
import { CONFIG } from '../../config.js';
import { getSharedBinaryAtlas } from '../../rendering/binary/sharedBinaryResources.js';
import { BinaryMaterial } from '../../rendering/binary/BinaryMaterial.js';
import { FlyingShip } from './FlyingShip.js';
import { debugLog } from '../../debug/debugLog.js';

/**
 * Red binary patrol ships orbiting above the player (low simulation cost).
 */
export class FlyingShipFleet {
  /** @param {{ config?: typeof CONFIG.flyingShips }} [options] */
  constructor({ config = CONFIG.flyingShips } = {}) {
    this.config = config;
    this.group = new THREE.Group();
    this.group.name = 'FlyingShipFleet';

    /** @type {FlyingShip[]} */
    this._ships = [];
    this._material = null;
    this._lastAnchor = { x: NaN, z: NaN };
    this._cellBase = 1_200_000;

    if (config.enabled) {
      this._build();
    }
  }

  _build() {
    const atlas = getSharedBinaryAtlas();
    this._material = new BinaryMaterial({
      atlas,
      palette: 'accent',
      overrides: {
        glowStrength: CONFIG.luminance.shipGlowStrength,
        flickerStrength: 0.14,
        scrollSpeed: 3.4,
        pulseSpeed: 1.2,
      },
    });

    let cellOffset = this._cellBase;
    let maxColumns = 8;
    const count = Math.max(0, Math.floor(this.config.count));

    for (let i = 0; i < count; i += 1) {
      const ship = new FlyingShip({
        material: this._material,
        seed: this.config.seed + i * 97,
        cellIdBase: cellOffset,
        config: this.config,
      });
      cellOffset += ship.cellsUsed;
      maxColumns = Math.max(maxColumns, ship.maxColumns);
      this._ships.push(ship);
      this.group.add(ship.root);
    }

    this._material.setColumns(maxColumns);
    debugLog('[FlyingShipFleet]', { ships: this._ships.length, cellBase: cellOffset });
  }

  /**
   * @param {number} delta
   * @param {{ x: number; y: number; z: number }} playerPosition
   */
  update(delta, playerPosition) {
    if (!this.config.enabled || !this._material) return;

    if (!CONFIG.graphics?.staticBinaryGlyphs) {
      this._material.update(delta);
    }

    const { x, z } = playerPosition;
    const moveThreshold = this.config.recenterDistance;
    if (
      Number.isNaN(this._lastAnchor.x) ||
      Math.hypot(x - this._lastAnchor.x, z - this._lastAnchor.z) > moveThreshold
    ) {
      this._lastAnchor.x = x;
      this._lastAnchor.z = z;
    }

    const anchorX = this._lastAnchor.x;
    const anchorZ = this._lastAnchor.z;
    for (const ship of this._ships) {
      ship.update(delta, anchorX, anchorZ);
    }
  }

  /**
   * Nearest ship for spatial audio (distance, pan, count in hear range).
   * @param {{ x: number; y: number; z: number }} listenerPosition
   * @param {number} listenYaw
   * @param {number} [maxDistance]
   */
  getAudioSnapshot(listenerPosition, listenYaw, maxDistance) {
    const max =
      maxDistance ?? CONFIG.audio?.ships?.maxHearDistance ?? 135;
    const maxSq = max * max;
    let nearestSq = Infinity;
    let nearestDx = 0;
    let nearestDy = 0;
    let nearestDz = 0;
    let audibleCount = 0;

    for (const ship of this._ships) {
      const wx = ship.root.position.x;
      const wy = ship.root.position.y;
      const wz = ship.root.position.z;
      const dx = wx - listenerPosition.x;
      const dy = wy - listenerPosition.y;
      const dz = wz - listenerPosition.z;
      const dSq = dx * dx + dy * dy + dz * dz;
      if (dSq <= maxSq) audibleCount += 1;
      if (dSq < nearestSq) {
        nearestSq = dSq;
        nearestDx = dx;
        nearestDy = dy;
        nearestDz = dz;
      }
    }

    if (!Number.isFinite(nearestSq) || nearestSq === Infinity) {
      return { distance: Infinity, pan: 0, audibleCount: 0 };
    }

    const worldAngle = Math.atan2(nearestDx, nearestDz);
    const pan = Math.sin(worldAngle - listenYaw);
    return {
      distance: Math.sqrt(nearestSq),
      pan,
      audibleCount,
    };
  }

  /** @param {number} boost */
  setEventBoost(boost) {
    this._material?.setEventBoost(boost);
  }

  dispose() {
    for (const ship of this._ships) ship.dispose();
    this._ships = [];
    this._material?.dispose();
    this._material = null;
    this.group.clear();
  }
}
